import React, { useCallback, useState } from "react";

import { Button, Checkbox, Divider, Modal, Text } from "@skydio/rivet-ui";

import { useChangeFlightDataFilesAccessRules } from "hooks/mutation/changeFlightDataFilesAccessRules";
import { displayMutationResults } from "utils/mutations";

interface ReleaseFlightDataFilesModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  flightIds?: string[] | null;
  fileIds?: string[] | null;
  showCjis?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
}

const ReleaseFlightDataFilesModal: React.FC<ReleaseFlightDataFilesModalProps> = ({
  isOpen,
  onOpenChange,
  onClose,
  flightIds,
  fileIds,
  showCjis,
  onConfirm,
}) => {
  const [markReleasedByCustomer, setMarkReleasedByCustomer] = useState(false);
  const [nonCjis, setNonCjis] = useState(false);

  const [changeFlightDataFilesAccessRules, loading] = useChangeFlightDataFilesAccessRules(
    displayMutationResults(response => response.changeFlightDataFilesAccessRules, {
      onSuccess: () => {
        onConfirm?.();
        setMarkReleasedByCustomer(false);
        setNonCjis(false);
        onOpenChange(false);
      },
      successMessage: () => "Data files released successfully",
    })
  );
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        onClose?.();
      }
    },
    [onOpenChange, onClose]
  );

  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const handleSubmit = useCallback(() => {
    changeFlightDataFilesAccessRules({
      flightIds: flightIds || [],
      fileIds: fileIds || [],
      fileUsage: markReleasedByCustomer ? "RELEASED_BY_CUSTOMER" : null,
      nonCji: nonCjis ? true : null,
    });
  }, [changeFlightDataFilesAccessRules, flightIds, fileIds, markReleasedByCustomer, nonCjis]);
  return (
    <Modal isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Grant Support Access to Skydio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p>
              Send data files to Skydio to support troubleshooting and product improvement. Once
              released, the data files cannot be taken back.
            </p>
            <Divider />
            <p>Select how to release the data.</p>
            <div className="mb-3">
              <Checkbox
                id="mark-released-by-customer-checkbox"
                isSelected={markReleasedByCustomer}
                onValueChange={setMarkReleasedByCustomer}
                className="items-start"
              >
                <div className="flex flex-col">
                  <span className="-m-1 font-normal">Release to Skydio</span>
                  <Text emphasis="secondary" className="mt-0.5 ">
                    Used for direct support & debugging
                  </Text>
                </div>
              </Checkbox>
            </div>
            {showCjis && (
              <div className="mb-3">
                <Checkbox
                  id="downdraft-cji-checkbox"
                  isSelected={nonCjis}
                  onValueChange={setNonCjis}
                  className="items-start"
                >
                  <div className="flex flex-col">
                    <span className="-m-1 font-normal">Mark as Non-CJI</span>
                    <Text emphasis="secondary" className="mt-0.5">
                      Allow access by Skydio personnel who haven&apos;t completed a CJIS background
                      check; by selecting this you assert this is not CJI.
                    </Text>
                  </div>
                </Checkbox>
              </div>
            )}
            <div className="border-t border-gray-300">
              <Text size="sm" emphasis="secondary">
                Your data is securely stored and only accessible to authorized Skydio teams.{" "}
                <a
                  href="https://support.skydio.com/hc/en-us/articles/4409148174107"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn More
                </a>
              </Text>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isDisabled={!markReleasedByCustomer && !nonCjis}
            isLoading={loading}
            onClick={handleSubmit}
          >
            Release Data
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

export default ReleaseFlightDataFilesModal;
